import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useGetDocumentsQuery, useLazyGetCurrentUserQuery } from 'api/main';
import {
    useSendAddressVerificationMutation,
    useStartAddressVerificationMutation,
} from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { getDocumentAvailability, getUserIdFromIri } from 'utils/helpers';
import { stepStatus, DOCUMENT_TYPES, iFrameKey, docTypes } from 'utils/constants';
import { useAddressCheck } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import TunnelStep from '../common/TunnelStep';
import TunnelBox from '../common/TunnelBox';
import AttestationSurHonneur from 'containers/AttestationSurHonneur/AttestationSurHonneur';
import { CustomSelect, DocumentLink, SubmitButton, Loader, ActionButton } from 'components';
import { Typography, MenuItem, Box } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useTunnelChangeHandlers } from 'utils/hooks';

const useStyles = makeStyles(theme => ({
    iframe: {
        minHeight: 600,
        border: `1px solid ${theme.palette.grey.A700}`,
    },
}));

const renderOption = ({ key, name }) => (
    <MenuItem key={key} value={key}>
        {name}
    </MenuItem>
);

const AddressCheck = props => {
    const { userStep, handleNextStep } = props;

    const { stepId } = useParams();
    const classes = useStyles();
    const adressCheckOk = useAddressCheck();

    const [getCurrentUser, { isFetching: userLoading }] = useLazyGetCurrentUserQuery();
    const [startAddressVerification, { isLoading: startLoader, data: jumioSession, reset }] =
        useStartAddressVerificationMutation();
    const [sendAddressVerification, { isLoading: sendLoader }] =
        useSendAddressVerificationMutation();
    const setTunnelChangesHandlers = useTunnelChangeHandlers();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const [docType, setDocType] = useState(DOCUMENT_TYPES[0]);
    const stepCompleted = useMemo(() => userStep?.status === stepStatus.COMPLETED, [userStep]);
    const stepVerified = useMemo(() => userStep?.status === stepStatus.VERIFIED, [userStep]);
    const stepFailed = useMemo(() => userStep?.status === stepStatus.FAILED, [userStep]);
    const unsolvedAlerts = useMemo(
        () => userStep?.alerts?.filter(a => !a.solved) || [],
        [userStep]
    );
    const [frameError, setFrameError] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [docTypeSubmitted, setDocTypeSubmitted] = useState(
        (stepCompleted || stepVerified) && !stepFailed
    );

    const isDocumentAvailable = useMemo(() => {
        return getDocumentAvailability(tunnelSubscription?.userTunnelSteps, stepId);
    }, [tunnelSubscription, stepId]);

    const needsDefaultMaxWidth = useMemo(
        () => ((stepCompleted || stepVerified) && !unsolvedAlerts.length) || !docTypeSubmitted,
        [stepCompleted, stepVerified, unsolvedAlerts, docTypeSubmitted]
    );

    const maxWidth = useMemo(() => {
        if (needsDefaultMaxWidth) return;
        return 1010;
    }, [needsDefaultMaxWidth]);

    const { data: documents = [], isFetching: documentsLoader } = useGetDocumentsQuery(
        { belongsTo: getUserIdFromIri(tunnelSubscription?.user), type: docTypes.FACTURE },
        { skip: !isDocumentAvailable }
    );

    const handleMessage = useCallback(
        e => {
            if (!e.data || typeof e.data !== 'string') return;
            try {
                const data = JSON.parse(e.data);
                const { value, privateKey } = data.payload;
                if (privateKey !== iFrameKey) return;

                if (value === 'success') {
                    sendAddressVerification(() => {
                        reset();
                        handleNextStep(true);
                    });
                }
                if (value === 'error') {
                    if (data.payload?.params?.errorCode === '310') {
                        // session expired: do something
                        setTimeout(() => {
                            getCurrentUser();
                        }, 1000);
                        setSessionExpired(true);
                    } else {
                        setFrameError(true);
                    }
                }
            } catch (error) {
                console.log(error);
            }
        },
        [sendAddressVerification, handleNextStep, reset, getCurrentUser]
    );

    const handleChange = useCallback(e => {
        const { value } = e.target;
        const documentType = DOCUMENT_TYPES.find(d => d.key === value);
        if (documentType) {
            setDocType(documentType);
        }
    }, []);

    useEffect(() => {
        getCurrentUser();
    }, [getCurrentUser]);

    useEffect(() => {
        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);

    const handleSubmitDocType = useCallback(() => {
        setDocTypeSubmitted(true);
        startAddressVerification(docType.type);
    }, [docType, startAddressVerification]);

    const handleAttestationSubmit = useCallback(() => {
        if (!jumioSession || !userStep) {
            startAddressVerification(docType.type);
        }
        setFrameError(false);
    }, [startAddressVerification, docType, jumioSession, userStep]);

    useEffect(
        () =>
            setTunnelChangesHandlers(
                afterSubmitCallback => () => afterSubmitCallback(),
                () => jumioSession,
                docTypeSubmitted && jumioSession
            ),
        [docTypeSubmitted, jumioSession, setTunnelChangesHandlers]
    );

    return (
        <TunnelStep
            maxWidth={maxWidth}
            title={texts.ADDRESS_CHECK}
            subtitle={
                <Typography variant="body1">
                    <i>{texts.DISCONNECT_REMINDER}</i>
                </Typography>
            }
        >
            <Loader loading={startLoader || userLoading || documentsLoader}>
                {docTypeSubmitted || stepCompleted ? (
                    <React.Fragment>
                        {jumioSession && !frameError && !stepCompleted && (
                            <iframe
                                width="100%"
                                className={classes.iframe}
                                title={texts.ADDRESS_CHECK}
                                allow="camera;fullscreen"
                                src={jumioSession.redirectUrl}
                            ></iframe>
                        )}
                        {frameError && <Typography align="center">{texts.FRAME_ERROR}</Typography>}
                        {stepCompleted && (
                            <Typography align="center">{texts.ATTEND_PROCESS}</Typography>
                        )}
                        {stepVerified && !unsolvedAlerts.length && (
                            <React.Fragment>
                                <Typography align="center">{texts.DOCUMENT_VERIFIE}</Typography>
                                {documents[0] && (
                                    <DocumentLink
                                        isDocumentAvailable={isDocumentAvailable}
                                        document={documents[0]}
                                        label={texts.FACTURE_DOC}
                                    />
                                )}
                            </React.Fragment>
                        )}
                        <AttestationSurHonneur onSubmit={handleAttestationSubmit} />
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {!!unsolvedAlerts.length && (
                            <Typography variant="body2" align="center" gutterBottom>
                                {texts.DOCUMENT_VERIFIE_AVEC_INCOHERENCES} {texts.MERCI_REESSAYER}
                            </Typography>
                        )}
                        <TunnelBox>
                            <CustomSelect
                                label={texts.DOCUMENT_TYPE}
                                value={docType.key}
                                onChange={handleChange}
                            >
                                {DOCUMENT_TYPES.map(renderOption)}
                            </CustomSelect>
                            {documents[0] && (
                                <DocumentLink
                                    isDocumentAvailable={isDocumentAvailable}
                                    document={documents[0]}
                                    label={texts.FACTURE_DOC}
                                />
                            )}
                            {(stepFailed || sessionExpired) && (
                                <Box sx={{ textAlign: 'center' }}>
                                    <ActionButton
                                        sx={{ margin: 'auto' }}
                                        onClick={handleSubmitDocType}
                                    >
                                        {texts.RETRY}
                                    </ActionButton>
                                </Box>
                            )}
                        </TunnelBox>
                    </React.Fragment>
                )}
            </Loader>
            {!docTypeSubmitted && !startLoader && !userLoading && !documentsLoader && (
                <SubmitButton
                    loading={sendLoader}
                    text={texts.SUIVANT}
                    onClick={
                        adressCheckOk && userStep ? () => handleNextStep(true) : handleSubmitDocType
                    }
                />
            )}
        </TunnelStep>
    );
};

export default AddressCheck;
